import { useEffect, useRef, useState } from 'react';

/**
 * A small menu, closed by clicking away or pressing Escape.
 *
 * Built rather than borrowed because the alternative on a card is three buttons, and three buttons
 * on every card is most of what a board shows. The trigger stays a real `<button>` with a label, so
 * it is reachable and announced; the menu itself is plain buttons, which is what they are.
 *
 * Closing on the list's own click is deliberate: every item in it either navigates or fires an
 * action, so a menu that stayed open afterwards would be covering the thing it just did.
 */
export function Menu({
  label,
  children,
  trigger,
  className,
}: {
  label: string;
  children: React.ReactNode;
  /** What opens it. The default is the `⋯` a card wears; a header gives it a labelled button. */
  trigger?: React.ReactNode;
  /** Extra classes on the trigger, so a header's menu can look like the buttons beside it. */
  className?: string;
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    const away = (pointerEvent: MouseEvent): void => {
      if (box.current && !box.current.contains(pointerEvent.target as Node)) setOpen(false);
    };

    const escape = (keyEvent: KeyboardEvent): void => {
      if (keyEvent.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', escape);

    return () => {
      document.removeEventListener('mousedown', away);
      document.removeEventListener('keydown', escape);
    };
  }, [open]);

  return (
    <div className="menu" ref={box}>
      <button
        type="button"
        className={className ?? 'menu__trigger'}
        aria-label={trigger ? undefined : label}
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        {trigger ?? '⋯'}
      </button>

      {open && (
        <div className="menu__list" role="menu" onClick={() => setOpen(false)}>
          {children}
        </div>
      )}
    </div>
  );
}
